<script setup lang="ts">
import { isUserFarFromCoords } from '~/utils/restaurantUtils'
import { Coords } from '~/lib/models/geolocation'
import { StoreRestaurantAreas } from '~/lib/interfaces/restaurant'
import { PickupStepType, PickupStep } from '~/lib/models/ui/modal-steps'
import { SpecialPaymentType } from '~/lib/services/store/payment/payment.dtos'
import { CounterType } from '~/lib/services/store/order/order.dto'

const isMobile = inject<Ref<boolean>>('isMobile')
const appStore = useAppStore()
const { navigateToHome } = useMcdRoutes()
const { trackOnCloseModal } = useGTM()

const loading = ref(true)
const actualStep = ref<PickupStepType>()
const areaSelected = ref<string>()
const selectedCounter = ref<CounterType>()
const dualPoint = ref<boolean>()
const saleTypeActive = ref<boolean>(false)
const userLocation = ref<Coords>()
const counterList = ref<CounterType[]>()

const serviceNumber = computed(() => {
  if (!appStore.currentOrder || !areaSelected.value || !appStore.currentOrder.area) return undefined
  if (appStore.currentOrder.area === StoreRestaurantAreas.AUT.toUpperCase()) {
    const orderId = appStore.currentOrder.id
    return orderId.slice(-4)
  } else if (appStore.currentOrder.area === StoreRestaurantAreas.MOP.toUpperCase()) {
    const orderNumber = appStore.currentOrder?.pos?.orderNumber
    return orderNumber ? orderNumber.toString() : undefined
  }
  return appStore.currentOrder?.serviceNumber
})

const inPersonPayment = computed(
  () => appStore.currentOrder?.paymentMethod === SpecialPaymentType.IN_PERSON
)

const saleType = computed(() => appStore.currentOrder?.saleType)

const setStep = (s: PickupStepType) => {
  actualStep.value = s
}

const onClose = () => {
  appStore.pickupMethodModal = false
  // If area is selected or has already serviceNumber refresh order detail and inProgressList view
  if (areaSelected.value || serviceNumber.value) appStore.pickupMethodRefresh = true
  appStore.setCurrentOrder(undefined)
  trackOnCloseModal()
}

const setArea = (e: {
  area?: string
  dualPoint: boolean
  saleTypeEnabled: boolean
  counters: CounterType[]
}) => {
  const { area, dualPoint: dualPointEnabled, saleTypeEnabled, counters } = e
  areaSelected.value = area
  dualPoint.value = dualPointEnabled
  saleTypeActive.value = area === StoreRestaurantAreas.MOP.toUpperCase() ? saleTypeEnabled : false
  if (!areaSelected.value) return
  if (
    areaSelected.value === StoreRestaurantAreas.MOP.toUpperCase() &&
    counters &&
    counters.length > 1
  ) {
    setStep(PickupStep.COUNTER_STEP)
    return
  }
  setStep(PickupStep.PREVIOUS_STEP)
}

const setCounterList = (counterListData: CounterType[]) => {
  counterList.value = counterListData
}

const setCounter = (e: { counter?: CounterType }) => {
  const { counter } = e
  selectedCounter.value = counter

  setStep(PickupStep.PREVIOUS_STEP)
}

const checkGeo = async () => {
  const permissionStatus = await getPermissionStatus('geolocation')
  return permissionStatus === 'granted'
}

const checkUserFarAway = async () => {
  const { latitude, longitude } = appStore.currentOrder!.deliveryPlace
  const isUserFarFromCoordsResult = await isUserFarFromCoords({
    coordinates: {
      lat: latitude,
      lng: longitude,
    },
    maxDistance: appStore.ecommerceConfig?.displayRestaurantConfirmation || 100,
  })
  return isUserFarFromCoordsResult
}

onMounted(async () => {
  // Validate
  if (!appStore.currentOrder) {
    await navigateToHome()
    onClose()
    loading.value = false
    return
  }
  // check geo
  const geoEnabled = await checkGeo()
  if (!geoEnabled) {
    actualStep.value = 'area_selection'
    setStep(PickupStep.AREA_SELECTION)
    loading.value = false
    return
  }
  // check user far from restaurant
  try {
    const { isUserFarAway, coords } = await checkUserFarAway()
    userLocation.value = coords
    if (!isUserFarAway) setStep(PickupStep.AREA_SELECTION)
    else setStep(PickupStep.FAR_AWAY)
  } catch (error) {
    setStep(PickupStep.AREA_SELECTION)
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  appStore.setCurrentOrder(undefined)
})
</script>

<template>
  <main data-test="pickup-methods-view">
    <Loader v-if="loading" full />
    <template v-else-if="actualStep">
      <PickupFarAwayModal
        v-if="actualStep === PickupStep.FAR_AWAY"
        :mobile="isMobile"
        @set-step="setStep"
        @close="onClose"
      />
      <PickupAreaSelectionModal
        v-else-if="actualStep === PickupStep.AREA_SELECTION"
        :mobile="isMobile"
        :user-location="userLocation"
        @set-counter-list="setCounterList"
        @set-area="setArea"
        @close="onClose"
      />
      <PickupCounterSelectionModal
        v-else-if="actualStep === PickupStep.COUNTER_STEP && counterList && counterList?.length"
        :counters="counterList"
        @set-step="setStep"
        @set-counter="setCounter"
        @close="onClose"
      />
      <LazyPickupAreaPreviousStep
        v-else-if="actualStep === PickupStep.PREVIOUS_STEP"
        :mobile="isMobile"
        :area="areaSelected"
        :sale-type-active="saleTypeActive"
        :selected-counter="selectedCounter"
        @set-counter-list="setCounterList"
        @set-step="setStep"
        @close="onClose"
      />
      <LazyPickupArea
        v-else
        :mobile="isMobile"
        :area="areaSelected"
        :service-number="serviceNumber"
        :dual-point="dualPoint"
        :in-person-payment="inPersonPayment"
        :sale-type="saleType"
        :selected-counter="selectedCounter"
        @close="onClose"
      />
    </template>
  </main>
</template>
